import {type ComponentProps, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {type Editor} from '@tiptap/core';
import {LinkIcon} from '@time-webkit/all/icons';
import {PopoverOverlay} from '@time-webkit/all/molecules/popover';
import {KEYBOARD_SHORTCUTS} from 'components/keyboard_shortcuts/keyboard_shortcuts_sequence';

import noop from '@tinkoff/utils/function/noop';
import {
    dataLinkPopoverTarget,
    dataLinkPreSelectionAttribute,
    LINK_POPOVER_EVENT,
    TimeWebkitLinkExtension,
} from 'packages/editor/src/extensions/link/link';
import {sendToStatist} from 'packages/statist';
import {useEventCallback} from 'usehooks-ts';

import {ShortcutTooltip} from '../../shortcut-tooltip';
import {ActionButton} from '../../action-button';

import {LinkPopoverForm} from '../link-popover-form/link-popover-form';
import {FloatingLinkPopoverForm} from '../floating-link-popover-form/floating-link-popover-form';
import {useLinkPreviewContext} from '../context/link-preview';

type LinkPopoverProps = {
    editor?: Editor;
    disabled?: boolean;
}

const focusScopeProps: ComponentProps<typeof PopoverOverlay>['focusScopeProps'] = {contain: false, restoreFocus: false, autoFocus: true};

export const LinkPopover = ({disabled, editor}: LinkPopoverProps) => {
    const {formatMessage} = useIntl();
    const [show, setShow] = useState(false);
    const [hasSelection, setHasSelection] = useState(false);
    const [decorationNode, setDecorationNode] = useState<Element | null>(null);
    const actionButtonRef = useRef<HTMLButtonElement | null>(null);
    const {handleLinkPreviewEvent, isOpened, isEditing, trigger} = useLinkPreviewContext();

    const isActive = show || (isOpened && (isEditing || trigger === 'caret'));
    const handleLinkPreviewOpenChange = () => {
        if (!editor) {
            return false;
        }

        let triggerNode: HTMLElement | null;

        const anchor = editor.state.selection.anchor;

        if (editor.state.selection.empty) {
            const resolvedPos = editor.state.doc.resolve(anchor);
            const mark = resolvedPos.marks().find((mark) => mark.type.name === TimeWebkitLinkExtension.name);

            if (!mark) {
                return false;
            }

            triggerNode = editor.view.domAtPos(anchor).node.parentElement;
            editor.commands.toggleLinkHighlight(true, triggerNode);
        } else {
            const selection = editor.state.selection;
            let hasLinkMark = true;
            editor.state.doc.nodesBetween(selection.from, selection.to, (node) => {
                if (node.isText) {
                    const mark = node.marks.find((m) => m.type.name === TimeWebkitLinkExtension.name);

                    if (!mark) {
                        hasLinkMark = false;
                    }
                }
            });

            if (!hasLinkMark) {
                return false;
            }

            editor.commands.toggleLinkHighlight(true);
            triggerNode = editor.view.dom.querySelector(`span[${dataLinkPopoverTarget}]`);
        }

        handleLinkPreviewEvent('toolbarclicked', triggerNode);
        return true;
    };
    const handlePopoverOpenChange = (isOpen: boolean) => {
        if (isOpen && handleLinkPreviewOpenChange()) {
            return;
        }

        setShow(isOpen);

        if (isOpen) {
            handleLinkPreviewEvent('disable');
            if (!editor?.state.selection) {
                setHasSelection(false);
                setDecorationNode(null);
                return;
            }

            editor?.commands.toggleLinkHighlight(true);
            setHasSelection(!editor.state.selection.empty);

            const decorationNode = editor.view.dom.querySelector(`span[${dataLinkPopoverTarget}]`);
            if (decorationNode) {
                setDecorationNode(decorationNode);
                return;
            }

            setDecorationNode(editor.view.dom.querySelector(`span[${dataLinkPreSelectionAttribute}]`));
        } else {
            handleLinkPreviewEvent('enable');
            editor?.commands.focus();
            editor?.commands.toggleLinkHighlight(false);
        }
    };

    const handleTogglePopover = useEventCallback(() => handlePopoverOpenChange(!show));

    const handlePress = () => {
        handlePopoverOpenChange(!show);
        sendToStatist('editor.format.tap', {formatType: 'link', hotkey: false});
    };

    const LinkPopoverComponent = hasSelection ? FloatingLinkPopoverForm : LinkPopoverForm;

    useEffect(() => {
        if (!editor) {
            return noop;
        }

        editor.on(LINK_POPOVER_EVENT, handleTogglePopover);

        return () => {
            editor.off(LINK_POPOVER_EVENT, handleTogglePopover);
        };

    // COMMENT: добалять editor в список зависимости не нужно
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownLinkNew} isHidden={disabled}>
                <ActionButton
                    isDisabled={disabled}
                    onPress={handlePress}
                    isActive={isActive}
                    ref={actionButtonRef}
                    aria-label={formatMessage(KEYBOARD_SHORTCUTS.msgMarkdownLinkNew.label)}
                    icon={<LinkIcon />}
                    data-qa='editor_button_link'
                />
            </ShortcutTooltip>
            <PopoverOverlay
                triggerNode={hasSelection ? decorationNode : actionButtonRef.current}
                placement={hasSelection ? 'bottom start' : 'top'}
                isOpen={show}
                offset={4}
                containerPadding={8}
                focusScopeProps={focusScopeProps}
                onOpenChange={handlePopoverOpenChange}
            >
                <LinkPopoverComponent
                    editor={editor}
                    onTogglePopover={handleTogglePopover}
                />
            </PopoverOverlay>
        </>
    );
};
